// A record's details: a centered dialog opened from the record's row or its
// menu. It reads as one page of a case file. The header names the record
// (its name, the identifier under it, its state) and holds the actions menu
// and the close control; it stays in place while the body scrolls. The body
// is sections ruled off from each other by a hairline, each under a small
// sentence-case label, with room between them. What can be done to a record
// is also on its row's menu; this is where it is read, and where the few
// settings that need a field are.

import { Box, Group, Modal, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';

import { spacing } from '../theme.gen';
import { DialogLoadBar } from './top-load-bar';

const HAIRLINE = '1px solid var(--mantine-color-default-border)';
// The inset every dialog's title and body take (theme.ts).
const DIALOG_INSET = spacing.xl;

export function PaneDrawer({ opened, onClose, children }: { opened: boolean; onClose: () => void; children: ReactNode }) {
  return (
    <Modal.Root opened={opened} onClose={onClose} size={560}>
      <Modal.Overlay />
      {/* No `style` here: on Modal.Content it lands on the positioning wrapper as well. The theme lays the content out through the Styles API. */}
      <Modal.Content><DialogLoadBar />{children}</Modal.Content>
    </Modal.Root>
  );
}

export function PaneHeader({ title, mono = false, subtitle, state, actions, divider = true }: {
  title: ReactNode;
  /** The title is an identifier: set in the mono face and never truncated. */
  mono?: boolean;
  /** The identifier under the name: an email, a slug. */
  subtitle?: ReactNode;
  /** The record's state: a badge, and a word or two beside it. */
  state?: ReactNode;
  actions?: ReactNode;
  /** The hairline under the header; off when the body opens with its own, as a tab bar does (PaneTabsList). */
  divider?: boolean;
}) {
  return (
    // Its own header rather than Modal.Header, whose fixed height and
    // padding are for a one-line title.
    <Box
      component="header"
      px={DIALOG_INSET}
      pt="md"
      pb={divider ? 'md' : 0}
      style={{ flex: 'none', display: 'flex', alignItems: 'flex-start', gap: 'var(--mantine-spacing-md)', background: 'var(--mantine-color-body)', borderBottom: divider ? HAIRLINE : undefined }}
    >
      <Stack gap={4} miw={0} flex={1}>
        <Modal.Title className={mono ? 'id' : undefined} style={{ overflowWrap: 'anywhere', whiteSpace: 'normal', lineHeight: 1.3 }}>{title}</Modal.Title>
        {subtitle && <Text size="sm" c="dimmed" style={{ overflowWrap: 'anywhere' }}>{subtitle}</Text>}
        {state && <Group gap="sm" mt={6} wrap="wrap">{state}</Group>}
      </Stack>
      <Group gap={2} wrap="nowrap" style={{ flex: 'none' }}>
        {actions}
        <Modal.CloseButton size={36} aria-label="Close details" />
      </Group>
    </Box>
  );
}

export function PaneBody({ children }: { children: ReactNode }) {
  return (
    <Modal.Body style={{ overflowY: 'auto', padding: 0 }}>
      <Box className="pane-sections" px={DIALOG_INSET} pb="xl">{children}</Box>
    </Modal.Body>
  );
}

/**
 * A pane's tab bar: edge to edge under its header, in place of the header's
 * hairline, and held at the top while the tab's content scrolls under it.
 * Pair it with `PaneHeader divider={false}`.
 */
export const PANE_TABS_LIST_STYLE = {
  marginInline: `calc(${DIALOG_INSET} * -1)`,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: 'var(--mantine-color-body)',
} as const;

